<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { ref } from 'vue';

defineProps({
    plans: {
        type: Array,
        default: () => []
    }
});

const billingCycle = ref('monthly');
</script>

<template>
    <div class="min-h-screen bg-white">
        <Head title="Paketler ve Fiyatlandırma — INSATEQ | Yapı Yönetim Platformu">
            <meta name="description" content="İhtiyacınıza uygun INSATEQ paketi seçin. Starter'dan Enterprise'a esnek planlar, şeffaf fiyatlandırma. 14 gün ücretsiz deneyin, kredi kartı gerekmez. İnşaat, site yönetimi ve BIM modülleri dahil." />
            <meta name="keywords" content="insateq fiyat, yapı yönetim yazılımı fiyatları, inşaat yazılımı paketleri, site yönetim yazılımı ücreti, BIM yazılımı fiyat, aylık abonelik, yıllık paket" />
            <meta name="author" content="INSATEQ" />

            <!-- Open Graph -->
            <meta property="og:title" content="Paketler ve Fiyatlandırma — INSATEQ" />
            <meta property="og:description" content="İhtiyacınıza uygun paketi seçin. 14 gün ücretsiz deneyin, kredi kartı gerekmez. Şeffaf fiyatlandırma, gizli ücret yok." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://insateq.com/pricing" />
            <meta property="og:image" content="/icon-512.png" />

            <!-- Twitter Card -->
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Paketler ve Fiyatlandırma — INSATEQ" />
            <meta name="twitter:description" content="İhtiyacınıza uygun paketi seçin. 14 gün ücretsiz deneyin, kredi kartı gerekmez." />
            <meta name="twitter:image" content="/icon-512.png" />
        </Head>

        <!-- Minimal Header -->
        <header class="bg-white/80 backdrop-blur-sm border-b border-gray-100">
            <div class="container mx-auto px-6 lg:px-12">
                <div class="flex justify-between items-center h-20">
                    <Link :href="route('home')" class="flex items-center space-x-2">
                        <div class="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <span class="text-xl font-semibold text-gray-900">INSATEQ</span>
                    </Link>

                    <nav class="hidden lg:flex items-center space-x-10">
                        <Link :href="route('home')" class="text-sm text-gray-600 hover:text-gray-900 transition">Ana Sayfa</Link>
                        <a href="#faq" class="text-sm text-gray-600 hover:text-gray-900 transition">SSS</a>
                        <Link :href="route('login')" class="text-sm text-gray-600 hover:text-gray-900 transition">Giriş</Link>
                        <Link :href="route('register')" class="bg-blue-600 text-white text-sm px-5 py-2 rounded hover:bg-blue-700 transition">
                            Ücretsiz Dene
                        </Link>
                    </nav>
                </div>
            </div>
        </header>

        <!-- Hero Section -->
        <section class="pt-20 pb-16 bg-gradient-to-b from-gray-50 to-white">
            <div class="container mx-auto px-6 lg:px-12">
                <div class="max-w-3xl mx-auto text-center">
                    <h1 class="text-4xl lg:text-5xl font-light text-gray-900 mb-6 leading-tight">
                        Basit ve şeffaf <span class="font-semibold">fiyatlandırma</span>
                    </h1>
                    <p class="text-lg lg:text-xl text-gray-600 font-light mb-10">
                        İşletmenizin ihtiyaçlarına uygun planı seçin. Tüm planlar 14 gün ücretsiz deneme ile gelir.
                    </p>

                    <!-- Billing Toggle -->
                    <div class="inline-flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                            @click="billingCycle = 'monthly'"
                            :class="[
                                'px-6 py-2 rounded-md text-sm font-medium transition',
                                billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
                            ]"
                        >
                            Aylık
                        </button>
                        <button
                            @click="billingCycle = 'yearly'"
                            :class="[
                                'px-6 py-2 rounded-md text-sm font-medium transition',
                                billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
                            ]"
                        >
                            Yıllık
                            <span class="ml-1 text-xs text-green-600 font-semibold">%20 indirim</span>
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <!-- Pricing Cards -->
        <section class="py-16 bg-white">
            <div class="container mx-auto px-6 lg:px-12">
                <div :class="[
                    'grid gap-6 max-w-6xl mx-auto',
                    plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'
                ]">
                    <div v-for="(plan, index) in plans" :key="plan.id"
                         :class="[
                             'p-8 rounded-lg bg-white border-2 transition-all hover:shadow-xl',
                             plan.is_popular || index === 1 ? 'border-blue-600 shadow-lg relative' : 'border-gray-200'
                         ]">
                        <div v-if="plan.is_popular || index === 1" class="absolute -top-4 left-1/2 transform -translate-x-1/2">
                            <span class="bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
                                Popüler
                            </span>
                        </div>

                        <div class="mb-6">
                            <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ plan.name }}</h3>
                            <p class="text-sm text-gray-600 font-light">{{ plan.description }}</p>
                        </div>

                        <div class="mb-6">
                            <div v-if="billingCycle === 'monthly'">
                                <span class="text-4xl font-light text-gray-900">₺{{ plan.monthly_price }}</span>
                                <span class="text-gray-600">/ay</span>
                            </div>
                            <div v-else>
                                <span class="text-4xl font-light text-gray-900">₺{{ Math.round(plan.yearly_price / 12) }}</span>
                                <span class="text-gray-600">/ay</span>
                                <div class="text-sm text-gray-500 mt-1">
                                    Yıllık ₺{{ plan.yearly_price }}
                                </div>
                            </div>
                        </div>

                        <div v-if="plan.trial_days" class="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-6">
                            <p class="text-xs text-green-800 font-medium">
                                {{ plan.trial_days }} gün ücretsiz deneme
                            </p>
                        </div>

                        <Link :href="route('register')"
                              :class="[
                                  'block w-full text-center py-3 rounded-lg transition font-medium mb-6',
                                  plan.is_popular || index === 1
                                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                              ]">
                            Başla
                        </Link>

                        <ul v-if="plan.features && plan.features.length > 0" class="space-y-3">
                            <li v-for="(feature, idx) in plan.features" :key="idx" class="flex items-start text-sm text-gray-600">
                                <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{{ feature }}</span>
                            </li>
                        </ul>

                        <div v-if="plan.max_users || plan.max_projects || plan.max_storage_gb" class="mt-6 pt-6 border-t border-gray-100">
                            <p class="text-xs text-gray-500 font-semibold mb-2">LİMİTLER</p>
                            <ul class="space-y-1 text-xs text-gray-600">
                                <li v-if="plan.max_users">
                                    <span v-if="plan.max_users === -1">Sınırsız kullanıcı</span>
                                    <span v-else>Maksimum {{ plan.max_users }} kullanıcı</span>
                                </li>
                                <li v-if="plan.max_projects">
                                    <span v-if="plan.max_projects === -1">Sınırsız proje</span>
                                    <span v-else>Maksimum {{ plan.max_projects }} proje</span>
                                </li>
                                <li v-if="plan.max_storage_gb">
                                    {{ plan.max_storage_gb }} GB depolama
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section id="faq" class="py-20 bg-gray-50">
            <div class="container mx-auto px-6 lg:px-12">
                <div class="max-w-3xl mx-auto">
                    <h2 class="text-3xl lg:text-4xl font-light text-gray-900 mb-4 text-center">
                        Sıkça sorulan <span class="font-semibold">sorular</span>
                    </h2>
                    <p class="text-lg text-gray-600 font-light text-center mb-12">
                        Aklınıza takılan sorular için yanıtlar
                    </p>

                    <div class="space-y-6">
                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                Ücretsiz deneme nasıl çalışır?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Herhangi bir plana kaydolduğunuzda, kredi kartı bilgisi girmeden 14 gün boyunca tüm özellikleri ücretsiz kullanabilirsiniz. Deneme süresi sonunda aboneliğinizi başlatabilir veya iptal edebilirsiniz.
                            </p>
                        </div>

                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                Plan değişikliği yapabilir miyim?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Evet, istediğiniz zaman planınızı yükseltebilir veya düşürebilirsiniz. Plan değişiklikleri anında geçerli olur ve fiyat farkı otomatik olarak hesaplanır.
                            </p>
                        </div>

                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                İptal politikanız nedir?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Herhangi bir taahhüt yoktur. Aboneliğinizi istediğiniz zaman iptal edebilirsiniz. İptal ettiğinizde, ödeme döneminizin sonuna kadar sistemi kullanmaya devam edebilirsiniz.
                            </p>
                        </div>

                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                Ödeme yöntemleri nelerdir?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Kredi kartı, banka kartı ve havale/EFT ile ödeme kabul ediyoruz. Yıllık ödemelerde %20 indirim uygulanmaktadır.
                            </p>
                        </div>

                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                Veri güvenliği nasıl sağlanıyor?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Tüm verileriniz SSL şifrelemesi ile korunur ve günlük olarak yedeklenir. ISO 27001 sertifikalı veri merkezlerinde barındırma yapıyoruz.
                            </p>
                        </div>

                        <div class="bg-white rounded-lg p-6 border border-gray-100">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">
                                Destek hizmeti var mı?
                            </h3>
                            <p class="text-gray-600 font-light leading-relaxed">
                                Tüm planlarda e-posta desteği mevcuttur. Profesyonel ve Kurumsal planlarda öncelikli destek ve telefon desteği de sunulmaktadır.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="py-20 bg-gradient-to-b from-gray-100 to-gray-50">
            <div class="container mx-auto px-6 lg:px-12">
                <div class="max-w-3xl mx-auto text-center">
                    <h2 class="text-3xl lg:text-4xl font-light text-gray-900 mb-6">
                        Hala karar veremediniz mi?
                    </h2>
                    <p class="text-lg text-gray-600 mb-8 font-light">
                        Ekibimiz size yardımcı olmak için burada. Size en uygun planı bulmamıza izin verin.
                    </p>
                    <div class="flex flex-col sm:flex-row justify-center gap-4">
                        <a href="mailto:info@insateq.com" class="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition font-medium">
                            Bize Ulaşın
                        </a>
                        <Link :href="route('register')" class="inline-block border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:border-gray-400 transition font-medium">
                            Hemen Başla
                        </Link>
                    </div>
                </div>
            </div>
        </section>

        <!-- Footer -->
        <footer id="contact" class="py-16 bg-white border-t border-gray-100">
            <div class="container mx-auto px-6 lg:px-12">
                <div class="grid md:grid-cols-4 gap-12 mb-12">
                    <div class="md:col-span-2">
                        <div class="flex items-center space-x-2 mb-4">
                            <div class="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <span class="text-xl font-semibold text-gray-900">INSATEQ</span>
                        </div>
                        <p class="text-sm text-gray-600 font-light max-w-sm">
                            Modern inşaat şirketleri için bulut tabanlı yönetim çözümü
                        </p>
                    </div>

                    <div>
                        <h4 class="text-sm font-semibold text-gray-900 mb-4">Ürün</h4>
                        <ul class="space-y-2 text-sm text-gray-600">
                            <li><Link :href="route('home')" class="hover:text-gray-900 transition">Özellikler</Link></li>
                            <li><Link :href="route('pricing')" class="hover:text-gray-900 transition">Fiyatlandırma</Link></li>
                            <li><a href="#" class="hover:text-gray-900 transition">Güvenlik</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 class="text-sm font-semibold text-gray-900 mb-4">Destek</h4>
                        <ul class="space-y-2 text-sm text-gray-600">
                            <li><a href="#faq" class="hover:text-gray-900 transition">SSS</a></li>
                            <li><a href="mailto:info@insateq.com" class="hover:text-gray-900 transition">İletişim</a></li>
                            <li><Link :href="route('login')" class="hover:text-gray-900 transition">Giriş Yap</Link></li>
                        </ul>
                    </div>
                </div>

                <div class="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
                    <p>&copy; 2025 INSATEQ. Tüm hakları saklıdır.</p>
                    <div class="flex space-x-6 mt-4 md:mt-0">
                        <a href="#" class="hover:text-gray-900 transition">Gizlilik</a>
                        <a href="#" class="hover:text-gray-900 transition">Şartlar</a>
                        <a href="#" class="hover:text-gray-900 transition">KVKK</a>
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>
